import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const TRANSIENT_MODES = Object.freeze(['impulse', 'bipolar', 'burst'] as const);
export type TransientMode = (typeof TRANSIENT_MODES)[number];

export interface TransientExciterOptions {
  readonly sampleRate: number;
  readonly mode?: TransientMode;
  readonly widthSamples?: number;
  readonly level?: number;
  readonly polarity?: -1 | 1;
}

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
};

export class TransientExciter {
  readonly sampleRate: number;
  private mode: TransientMode;
  private widthSamples: number;
  private level: number;
  private polarity: -1 | 1;
  private gateHigh = false;
  private position = -1;

  constructor(options: TransientExciterOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.mode = options.mode ?? 'bipolar';
    if (!(TRANSIENT_MODES as readonly string[]).includes(this.mode)) throw new RangeError('mode is invalid');
    this.widthSamples = options.widthSamples ?? 4;
    this.level = options.level ?? 0.8;
    this.polarity = options.polarity ?? 1;
    this.validate();
  }

  reset(): void { this.gateHigh = false; this.position = -1; }

  setParameter(id: string, value: number): void {
    if (id === 'mode') {
      if (!Number.isInteger(value) || value < 0 || value >= TRANSIENT_MODES.length) throw new RangeError('mode must be an integer in [0, 2]');
      this.mode = TRANSIENT_MODES[value]!; return;
    }
    if (id === 'width-samples') {
      if (!Number.isInteger(value)) throw new RangeError('width-samples must be an integer');
      assertRange(id, value, 1, 2048); this.widthSamples = value; return;
    }
    if (id === 'level') { assertRange(id, value, 0, 0.95); this.level = value; return; }
    if (id === 'polarity') {
      if (value !== -1 && value !== 1) throw new RangeError('polarity must be -1 or 1');
      this.polarity = value; return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(output: Float32Array, trigger: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || output.length < frames || trigger.length < frames) throw new RangeError('buffers are too short for frames');
    for (let index = 0; index < frames; index += 1) {
      const high = trigger[index]! > 0;
      if (high && !this.gateHigh) this.position = 0;
      this.gateHigh = high;
      output[index] = this.next();
    }
  }

  private validate(): void {
    if (!Number.isInteger(this.widthSamples)) throw new RangeError('widthSamples must be an integer');
    assertRange('widthSamples', this.widthSamples, 1, 2048);
    assertRange('level', this.level, 0, 0.95);
    if (this.polarity !== -1 && this.polarity !== 1) throw new RangeError('polarity must be -1 or 1');
  }

  private next(): number {
    if (this.position < 0) return 0;
    const p = this.position++;
    const width = this.mode === 'impulse' ? 1 : this.widthSamples;
    if (this.position >= width) this.position = -1;
    if (this.mode === 'impulse') return this.polarity * this.level;
    if (this.mode === 'bipolar') {
      const halfWidth = Math.floor(width / 2);
      if ((width & 1) === 1 && p === halfWidth) return 0;
      return this.polarity * this.level * (p < halfWidth ? 1 : -1);
    }
    if (width === 1) return this.polarity * this.level;
    const phase = p / (width - 1);
    const window = 0.5 - 0.5 * Math.cos(2 * Math.PI * phase);
    return this.polarity * this.level * window * Math.sin(8 * Math.PI * phase);
  }
}

class TransientModuleInstance implements DspModuleInstance {
  private readonly exciter: TransientExciter;
  constructor(sampleRate: number, private readonly maxBlockSize: number) { this.exciter = new TransientExciter({ sampleRate }); }
  reset(): void { this.exciter.reset(); }
  setParameter(id: string, value: number): void { this.exciter.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const trigger = block.controls.trigger; const output = block.outputs.out;
    if (!trigger || trigger.length < block.frames) throw new RangeError('trigger control buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.exciter.processInto(output, trigger, block.frames);
  }
}

export const mod005 = defineDspModule({
  descriptor: {
    id: 'mir.mod-005', name: 'Impulse / click / transient exciter', version: '0.1.0',
    inputs: [{ id: 'trigger', kind: 'control', channels: 1 }], outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'mode', defaultValue: 1, minValue: 0, maxValue: 2, unit: 'enum' },
      { id: 'width-samples', defaultValue: 4, minValue: 1, maxValue: 2048, unit: 'samples' },
      { id: 'level', defaultValue: 0.8, minValue: 0, maxValue: 0.95, unit: 'ratio' },
      { id: 'polarity', defaultValue: 1, minValue: -1, maxValue: 1, unit: 'sign' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid DSP instance context');
    return new TransientModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
